import { useEffect, useRef } from "react";
import { planAutoSelect } from "../lib/series/autoSelectPlan.js";

/**
 * Auto-plot the confidently-relevant signals for the active question — the
 * "obvious match → auto-select" path. Consumes useSignalSuggestions' output and
 * reconciles the chart's series to it: replace the PREVIOUS question's
 * auto-plotted series, PRESERVE the user's manual picks, fill up to the 2-slot
 * cap. Fetch is by ssid, so a corpus name that wouldn't resolve still plots.
 *
 * OWNERSHIP: only when `autoSelectEnabled` (the deployment flag) does this hook
 * OWN `_auto` series — otherwise the legacy keyword auto-populate owns them and
 * we must not touch them. When enabled it reconciles ONCE PER QUESTION, but only
 * after results for THAT question have arrived (`resultQuestion === question`) —
 * so a new question that legitimately plots NOTHING still clears the prior
 * question's auto-plot (empty picks → the plan removes the stale `_auto` series),
 * while a still-pending fetch (empty autoSelect, resultQuestion lagging) is left
 * alone. The once-per-question guard means a manual removal isn't re-applied.
 *
 * @param {{ question?: string, autoSelect?: Array, autoSelectEnabled?: boolean,
 *           resultQuestion?: string|null, series: object, addSeries: Function,
 *           removeSeries: Function, theme?: string }} args
 */
export function useAutoSelectSignals({
  question,
  autoSelect,
  autoSelectEnabled,
  resultQuestion,
  series,
  addSeries,
  removeSeries,
  theme,
}) {
  const appliedQuestionRef = useRef(null);
  useEffect(() => {
    if (!autoSelectEnabled) return; // feature off → legacy owns _auto series; hands off
    const q = (question || "").trim();
    if (!q) return;
    // Act only once results for THIS question are in (distinguishes "pending" from
    // "arrived, agent picked nothing"), and only once per question.
    if (resultQuestion !== q) return;
    if (appliedQuestionRef.current === q) return;
    appliedQuestionRef.current = q;
    const { toRemove, toAdd } = planAutoSelect(autoSelect, { series, theme });
    for (const name of toRemove) removeSeries(name);
    for (const a of toAdd) addSeries(a.name, a.color, /* isAuto */ true, a.ssid);
  }, [
    question,
    autoSelect,
    autoSelectEnabled,
    resultQuestion,
    series,
    addSeries,
    removeSeries,
    theme,
  ]);
}
